/**
 * Doubt Service for iLearn Student App
 * Handles all API calls related to student doubts
 */

import config from '../_config/config';

class DoubtService {
  /**
   * Get authorization headers with JWT token.
   *
   * Atlas users register via OTP and the flow writes both `user` and
   * `atlas_user` to localStorage. The Atlas mini-app's PrivateRoute now
   * requires both keys, but a main-app logout can still clear `user` while
   * an already-mounted Atlas page keeps making calls — falling back to
   * `atlas_user` keeps those from hitting "Authorization header is missing".
   */
  getAuthHeaders() {
    const token = this._readToken('user') || this._readToken('atlas_user');

    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  _readToken(storageKey) {
    const raw = localStorage.getItem(storageKey);
    if (!raw || raw === 'undefined') return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.token || null;
    } catch (error) {
      console.error(`Failed to parse ${storageKey}:`, error);
      return null;
    }
  }

  /**
   * Create a new doubt
   * @param {Object} doubt - Doubt data
   * @returns {Promise<Object>} Created doubt
   */
  async createDoubt(doubt) {
    const response = await fetch(`${config.nodeSiteUrl}v1/doubts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(doubt)
    });

    if (!response.ok) {
      throw new Error(`Failed to create doubt: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Get student's doubts
   * @param {number} studentId - Student ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} List of doubts
   */
  async getStudentDoubts(studentId, filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.courseId) queryParams.append('courseId', filters.courseId.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.offset) queryParams.append('offset', filters.offset.toString());

    const response = await fetch(
      `${config.nodeSiteUrl}v1/doubts/student/${studentId}?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch doubts: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Update doubt (for feedback, escalation, etc.)
   * @param {string} doubtId - Doubt ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated doubt
   */
  async updateDoubt(doubtId, updates) {
    const response = await fetch(`${config.nodeSiteUrl}v1/doubts/${doubtId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error(`Failed to update doubt: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Escalate doubt to teacher
   * @param {string} doubtId - Doubt ID
   * @param {string} teacherId - Optional teacher ID
   * @param {string} escalationReason - Optional note from student
   * @returns {Promise<Object>} Updated doubt
   */
  async escalateDoubt(doubtId, teacherId, escalationReason, target = 'teacher') {
    const response = await fetch(`${config.nodeSiteUrl}v1/doubts/${doubtId}/escalate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        escalatedTo: teacherId,
        escalationTarget: target,
        ...(escalationReason && { escalationReason })
      })
    });

    if (!response.ok) {
      // Surface backend message (e.g. consent-required) to the caller.
      let msg = `Failed to escalate doubt: ${response.statusText}`;
      try {
        const err = await response.json();
        msg = err?.error?.message || msg;
      } catch (_) { /* ignore */ }
      throw new Error(msg);
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Volunteer consent status for a student.
   * @param {number} studentId
   * @returns {Promise<{consented: boolean, latest: object|null}>}
   */
  async getVolunteerConsent(studentId) {
    const response = await fetch(`${config.nodeSiteUrl}v1/volunteers/consent/${studentId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch consent status');
    const data = await response.json();
    return data.data || data;
  }

  /**
   * Grant volunteer consent for a student (student/guardian flow).
   * @param {Object} payload - { studentId, method, grantedByRelation?, consentText? }
   */
  async grantVolunteerConsent(payload) {
    const response = await fetch(`${config.nodeSiteUrl}v1/volunteers/consent`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Failed to record consent');
    const data = await response.json();
    return data.data || data;
  }

  /**
   * Submit feedback on AI response
   * @param {string} doubtId - Doubt ID
   * @param {string} feedback - Feedback type
   * @param {string} comment - Optional comment
   * @returns {Promise<Object>} Updated doubt
   */
  async submitFeedback(doubtId, feedback, comment) {
    return this.updateDoubt(doubtId, {
      studentFeedback: feedback,
      feedbackComment: comment
    });
  }

  /**
   * Ask AI for doubt clarification with context
   * @param {string} question - Student's question
   * @param {Object} context - Context information
   * @param {Array} conversationHistory - Previous conversation
   * @returns {Promise<Object>} AI response with metadata
   */
  async askAI(question, context, conversationHistory = [], configType, signal) {
    const response = await fetch(`${config.nodeSiteUrl}v1/gen-ai/doubt-clarification`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        question,
        context,
        conversationHistory,
        ...(configType && { configType })
      }),
      ...(signal && { signal })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const err = new Error(
        errorData?.error?.message || `Failed to get AI response: ${response.statusText}`
      );
      err.status = response.status;
      err.errorCode = errorData?.error?.code;
      throw err;
    }

    const data = await response.json();
    return data;
  }

  /**
   * Get doubt statistics
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Statistics
   */
  async getDoubtStatistics(filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.batchId) queryParams.append('batchId', filters.batchId.toString());
    if (filters.courseId) queryParams.append('courseId', filters.courseId.toString());
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo.toISOString());

    const response = await fetch(
      `${config.nodeSiteUrl}v1/doubts/statistics?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch statistics: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  }
}

export default new DoubtService();
